import { useEffect, useState } from 'react';
import { api, type Taak } from '../api/client';

/**
 * Meldt lopende en afgeronde taken onder in het scherm.
 *
 * De gebruiker ziet altijd wat er op de achtergrond gebeurt, inclusief de
 * mogelijkheid om een geslaagde export direct te downloaden.
 */
export interface TaakMeldingenProps {
  taken: Taak[];
  onOpnieuw: (taak: Taak) => void;
}

export function TaakMeldingen({ taken, onOpnieuw }: TaakMeldingenProps) {
  const [actuele, setActuele] = useState<Taak[]>(taken);

  useEffect(() => {
    setActuele((huidig) => {
      const samen = [...huidig];
      for (const taak of taken) {
        if (!samen.some((t) => t.id === taak.id)) samen.push(taak);
      }
      return samen;
    });
  }, [taken]);

  // Poll lopende taken tot ze klaar zijn.
  useEffect(() => {
    const lopend = actuele.filter((t) => t.status === 'wacht' || t.status === 'bezig');
    if (lopend.length === 0) return;
    const id = window.setTimeout(async () => {
      const bijgewerkt = await Promise.all(
        lopend.map(async (t) => {
          try {
            return await api.taak(t.id);
          } catch {
            return t;
          }
        }),
      );
      setActuele((huidig) => huidig.map((t) => bijgewerkt.find((b) => b.id === t.id) ?? t));
    }, 1200);
    return () => window.clearTimeout(id);
  }, [actuele]);

  if (actuele.length === 0) return null;

  return (
    <div className="taakmeldingen">
      {actuele.map((taak) => (
        <div key={taak.id} className={`taakmelding taakmelding--${taak.status}`}>
          <span className="taakmelding__tekst">
            <strong>{taak.soort === 'export' ? 'Export' : taak.soort}</strong>{' '}
            {taak.status === 'bezig' || taak.status === 'wacht'
              ? `${taak.fase ?? 'bezig'} — ${Math.round((taak.voortgang ?? 0) * 100)}%`
              : taak.status === 'klaar'
                ? 'gereed'
                : (taak.foutMelding ?? 'mislukt')}
          </span>
          {(taak.status === 'bezig' || taak.status === 'wacht') && (
            <span className="taakmelding__balk">
              <span style={{ width: `${Math.round((taak.voortgang ?? 0) * 100)}%` }} />
            </span>
          )}
          {taak.status === 'klaar' && taak.soort === 'export' && Boolean((taak.resultaat as { opslagSleutel?: string } | null)?.opslagSleutel) && (
            <span className="taakmelding__hint">
              De downloadknop staat in het exportvenster.
            </span>
          )}
          <button type="button" className="knop knop--klein knop--stil" onClick={() => onOpnieuw(taak)}>
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
